import type { CalendarItem } from './calendar-item';

// How the Calendar composes its date words out of the locale's own formatters. The formatters are
// per-surface (Intl here, DateFormatter / ICU on the phones); the composition is not, so it lives here and
// the shared contract runs it with stub formatters (`native-apps/shared/calendar/README.md`).

/** The locale formatters the composition is built from. */
export interface CalendarFormatParts {
  /** "Wednesday, October 21". */
  fullDay: (at: number) => string;
  /** "10:00 AM". */
  time: (at: number) => string;
  /** "10:00 – 11:30 AM". */
  timeRange: (start: number, end: number) => string;
  /** "$25.50". */
  price: (amount: string, currency: string) => string;
}

/** "Tuesday, October 21, 2:00 – 3:00 PM": a stretch of time picked on the grid, or just its day. */
export function formatWhen(parts: CalendarFormatParts, start: number, end?: number): string {
  if (end === undefined) return parts.fullDay(start);
  return `${parts.fullDay(start)}, ${end > start ? parts.timeRange(start, end) : parts.time(start)}`;
}

/** "10:00 – 11:30", or the one time for a point in time. */
export function formatSpan(parts: CalendarFormatParts, item: Pick<CalendarItem, 'start' | 'end'>): string {
  return item.end > item.start ? parts.timeRange(item.start, item.end) : parts.time(item.start);
}

/**
 * An item's When, as the details sheet shows it: the day of an all-day item (first – last for several), or the
 * day and its times.
 */
export function itemWhen(parts: CalendarFormatParts, item: Pick<CalendarItem, 'allDay' | 'start' | 'end'>): string {
  if (!item.allDay) return `${parts.fullDay(item.start)} · ${formatSpan(parts, item)}`;
  return item.end - item.start > 86_400_000 ? `${parts.fullDay(item.start)} – ${parts.fullDay(item.end - 1)}` : parts.fullDay(item.start);
}

/**
 * The `{when}` of "Change “{title}” ({when})". An all-day item says its days, as its When row does — given its
 * midnight-to-midnight bounds as times it read "Wednesday, October 21, 10/21/2026, 12:00 AM – 10/22/2026,
 * 12:00 AM". A timed one says its day and times; a point in time, its day and the one time.
 */
export function editSeedWhen(parts: CalendarFormatParts, item: Pick<CalendarItem, 'allDay' | 'start' | 'end'>): string {
  return item.allDay ? itemWhen(parts, item) : formatWhen(parts, item.start, item.end);
}
